<template>
    <div class="license">
        <section class="section">
            <div class="section-row">
                <div class="section-row__col section-row__col--one-half">
                    <div class="label">
                        {{ trans('licenses.label.license_plan_of_client') }}
                    </div>

                    <div class="value">
                        {{ trans('clients.label.' + client.license_plan) }}
                    </div>
                </div>
            </div>

            <div class="section-row">
                <div class="section-row__col section-row__col--one-half">
                    <div class="label">
                        {{ trans('licenses.label.available_licenses') }}

                        <template
                            v-if="
                                (!isAdmin && !parentUser && canBuyLicenses) ||
                                    ((isAdmin || parentUser) &&
                                        canBuyLicenses) ||
                                    canTransferLicenses
                            "
                        >
                            (<a
                                v-if="!isAdmin && !parentUser && canBuyLicenses"
                                :href="
                                    route(
                                        'tenant.clients.licenses.bookings.create',
                                        {
                                            client: client.id,
                                            license: license.id,
                                        }
                                    )
                                "
                                class="link link--primary"
                            >
                                {{ trans('licenses.label.buy_licenses') }}
                                {{
                                    ((isAdmin || parentUser) &&
                                        canBuyLicenses) ||
                                    canTransferLicenses
                                        ? '|'
                                        : ''
                                }}
                            </a>

                            <a
                                v-if="(isAdmin || parentUser) && canBuyLicenses"
                                :href="
                                    route(
                                        'tenant.clients.licenses.bookings.create',
                                        {
                                            client: client.id,
                                            license: license.id,
                                        }
                                    )
                                "
                                class="link link--primary"
                            >
                                {{ trans('licenses.label.add_licenses') }}
                                {{ canTransferLicenses ? '|' : '' }}
                            </a>

                            <a
                                v-if="canTransferLicenses"
                                :href="
                                    route('tenant.bookings.transfer', {
                                        client: client.id,
                                        license: license.id,
                                    })
                                "
                                class="link link--primary"
                            >
                                {{
                                    trans('licenses.label.transfer_licenses')
                                }} </a
                            >)
                        </template>
                    </div>

                    <div class="value">
                        {{ license.amount }}
                    </div>
                </div>

                <div class="section-row__col section-row__col--one-half">
                    <div class="label">
                        {{ trans('licenses.label.overdue_assignments') }}
                    </div>

                    <div class="value">
                        {{
                            license.overdue_assignments ||
                                trans('licenses.label.none')
                        }}
                    </div>
                </div>
            </div>

            <div class="section-row">
                <div class="section-row__col section-row__col--one-half">
                    <div class="label">
                        {{ trans('licenses.label.expiration_date') }}
                    </div>

                    <div class="value">
                        {{ formatDate(license.expiration_date) }}
                    </div>
                </div>

                <div class="section-row__col section-row__col--one-half">
                    <div class="label">
                        {{ trans('licenses.label.expiration_period') }}
                    </div>

                    <div class="value">
                        {{ license.expiration_period }}
                    </div>
                </div>
            </div>

            <div class="section-row">
                <div
                    class="section-row__col section-row__col--one-half"
                    v-for="(value, key) in licenseConsumption"
                    :key="key"
                >
                    <div class="label">
                        {{ key }}
                    </div>

                    <div class="value">
                        {{ value || trans('licenses.label.none') }}
                    </div>
                </div>
            </div>

            <div class="section-row">
                <div class="section-row__col section-row__col--one-half">
                    <label class="label">
                        {{ trans('licenses.label.required_until.select') }}
                    </label>
                    <input
                        type="date"
                        name="date"
                        class="input"
                        v-model="requiredUntilDate"
                        :min="requiredUntilMin"
                        :max="requiredUntilMax"
                        @change="onChangeRequiredUntilDate"
                        :disabled="requiredUntilDisabled"
                        required
                    />
                </div>
                <div class="section-row__col section-row__col--one-half">
                    <div class="label">
                        {{
                            trans('licenses.label.required_until.value', {
                                date: requiredUntilDateTrans,
                            })
                        }}
                    </div>

                    <div class="value">
                        {{ requiredUntilAmount }}
                    </div>
                </div>
            </div>
        </section>

        <div class="license__tiles">
            <div class="license__tile" v-if="canConfig">
                <license-tile
                    image="/images/license_settings.svg"
                    :title="trans('bookings.tile.configuration.title')"
                    :body="trans('bookings.tile.configuration.body')"
                    :linkPrimary="
                        route('tenant.clients.licenses.show', {
                            client: client.id,
                            license: license.id,
                        })
                    "
                    :titleLinkPrimary="
                        trans('bookings.tile.configuration.link')
                    "
                >
                </license-tile>
            </div>

            <div class="license__tile" v-if="canSeeBookings">
                <license-tile
                    image="/images/license_bookings.svg"
                    :title="trans('bookings.tile.bookings.title')"
                    :body="trans('bookings.tile.bookings.body')"
                    :linkPrimary="
                        route('tenant.clients.licenses.bookings.index', {
                            client: client.id,
                            license: license.id,
                        })
                    "
                    :titleLinkPrimary="trans('bookings.tile.bookings.link')"
                >
                </license-tile>
            </div>

            <div class="license__tile" v-if="canSeeBookingHistory">
                <license-tile
                    image="/images/license_history.svg"
                    :title="trans('bookings.tile.booking_history.title')"
                    :body="trans('bookings.tile.booking_history.body')"
                    :linkPrimary="
                        route('tenant.bookings.history', {
                            client: client.id,
                            license: license.id,
                        })
                    "
                    :titleLinkPrimary="
                        trans('bookings.tile.booking_history.link')
                    "
                >
                </license-tile>
            </div>
        </div>
    </div>
</template>

<script>
var moment = require('moment');

export default {
    props: [
        'client',
        'license',
        'licenseConsumption',
        'canConfig',
        'canBuyLicenses',
        'canSeeBookings',
        'canSeeBookingHistory',
        'canTransferLicenses',
        'parentUser',
        'isAdmin',
    ],

    data() {
        const tomorrow = Date.now() + 24 * 60 * 60 * 1000;
        const nextYear = Date.now() + 365 * 24 * 60 * 60 * 1000;
        return {
            requiredUntilDisabled: false,
            requiredUntilDate: new Date(tomorrow).toISOString().substr(0, 10),
            requiredUntilMin: new Date(tomorrow).toISOString().substr(0, 10),
            requiredUntilMax: new Date(nextYear).toISOString().substr(0, 10),
            requiredUntilAmount: 0,
        };
    },

    computed: {
        requiredUntilDateTrans() {
            return moment(this.requiredUntilDate).format('DD.MM.YYYY');
        },
    },

    methods: {
        formatDate(date) {
            return moment(date).format('DD.MM.YYYY');
        },
        onChangeRequiredUntilDate() {
            this.requiredUntilDisabled = true;
            axios
                .post(
                    this.route('tenantapi.clients.licenses.required_until', {
                        client: this.client.id,
                    }),
                    {
                        date: this.requiredUntilDate,
                    }
                )
                .then(response => {
                    this.requiredUntilAmount = response.data.license_amount;
                    this.requiredUntilDisabled = false;
                });
        },
    },

    mounted() {
        this.onChangeRequiredUntilDate();
    },
};
</script>
